/*******************************************************************************
  설  명 : 고객센터 > 정기구독안내
  작성자 : 송영석
  작성일 : 2020-06-08
*******************************************************************************/
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbDateStruct, NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';

import { UtilService } from '@app/service/util.service';
import { CommonCodeService } from '@admin/service/common.code.service';
import { CommonService } from '@app/service/common.service';

import { HAssociationService } from '@home/service/association.service';

import { ModalMonthComponent } from '@components/modal-month/modal-month.component';
import { O2OZCMS } from '@app/components/o2oz-cms/o2oz-cms.component';

const options: NgbModalOptions = {
  backdrop: 'static',
  keyboard: false,
  // size: 'xl',
  centered: true,
  windowClass: 'modal-fadeInDown'
};

@Component({
  selector: 'app-magazine-subscription',
  templateUrl: './magazine-subscription.component.html',
  styleUrls: ['./magazine-subscription.component.scss']
})
export class MagazineSubscriptionComponent extends O2OZCMS implements OnInit {

  /*******************************************************************************
    설명 : 전역 변수 선언부
  *******************************************************************************/

  public associationStatusList: any = [];

  public form: FormGroup;
  public formErrors = {};

  private dateModel: NgbDateStruct;

  daumAddressOptions =  {
    class: ['btn', 'zip-btn']
  };

  /*******************************************************************************
    설  명 : 폼생성
    입력값 : 없음
    리턴값 : 없음
  *******************************************************************************/
  buildForm(): void {
    this.form = this.formBuilder.group({
      company: ['', [Validators.required] ],
      name: ['', [Validators.required] ],
      dept: ['', [Validators.required] ],
      chargePerson: ['', [Validators.required] ],
      subscriptionType: ['', [Validators.required] ],
      ho1: ['', [] ],
      date1: ['', [] ],
      ho2: ['', [] ],
      date2: ['', [] ],
      bu1: ['', [] ],
      ho3: ['', [] ],
      date3: ['', [] ],
      bu2: ['', [] ],
      zipcode: ['', [Validators.required] ],
      address: ['', [Validators.required] ],
      addressDetail: ['', [Validators.required] ],
      tel1: ['', [Validators.required] ],
      tel2: ['', [Validators.required] ],
      tel3: ['', [Validators.required] ],
      content: ['', [] ],
    });

    this.form.valueChanges.subscribe(data => {
      this.utilService.updateFormErrors( this.form, this.formErrors );
    });
  }

  /*******************************************************************************
    설  명 : 생성자
    입력값 : 없음
    리턴값 : 없음
  *******************************************************************************/
  constructor(
    router: Router,
    commonService: CommonService,
    activatedRoute: ActivatedRoute,
    toastrService: ToastrService,
    private utilService: UtilService,
    private formBuilder: FormBuilder,
    private modalService: NgbModal,
    private commonCodeService: CommonCodeService,
    private associationService: HAssociationService
  ) {
    // 상위 클래스에 router 전달
    super(router, commonService, activatedRoute, toastrService);

    this.buildForm();
  }

  ngOnInit(): void {
    // 공통코드 가져오기
    this.getCommonList();
  }

  /*******************************************************************************
    설  명 : 공통코드 - 협회지구독구분 가져오기
    입력값 : 없음
    리턴값 : 없음
  *******************************************************************************/
  async getCommonList() {
    await this.commonCodeService.getCommonList('ASB').then( response => {
      if ( response.ResultCode ) {
        this.associationStatusList = response.data;
      } else {
        this.associationStatusList = [];
      }
    });
  }

  /*******************************************************************************
    설  명 : 협회지 구독 정보 저장하기
    입력값 : 없음
    리턴값 : 없음
  *******************************************************************************/
  setAssociationSubscriptionSave() {
    this.utilService.makeFormDirtyAndUpdateErrors(this.form, this.formErrors);

    if ( this.form.valid ) {
      this.associationService.setAssociationSubscriptionSave( this.form ).then( response => {
        if ( response.ResultCode ) {
          this.toastrService.success( response.ResultMessage, '정기구독 정보 저장');

          this.router.navigate(['/customerCenter/magazine']);
        } else {
          this.toastrService.error( response.ResultMessage, '정기구독 정보 저장');
        }
      });
    } else {
      this.toastrService.error('필수 입력항목을 확인하시기 바랍니다.', '정기구독 정보 저장');
    }
  }

  /*******************************************************************************
    설  명 : 년월선택 모달창 열기
    입력값 : 없음
    리턴값 : 없음
  *******************************************************************************/
  setMonthPicker1() {
    const modalRef = this.modalService.open(ModalMonthComponent, options);

    modalRef.componentInstance.emitData.subscribe(($e) => {
      this.form.get('date1').setValue($e);
    });

    modalRef.result.then((result) => {
    }, (reason) => {
    });
  }

  /*******************************************************************************
    설  명 : 년월선택 모달창 열기
    입력값 : 없음
    리턴값 : 없음
  *******************************************************************************/
  setMonthPicker2() {
    const modalRef = this.modalService.open(ModalMonthComponent, options);

    modalRef.componentInstance.emitData.subscribe(($e) => {
      this.form.get('date2').setValue($e);
    });

    modalRef.result.then((result) => {
    }, (reason) => {
    });
  }

  /*******************************************************************************
    설  명 : 년월선택 모달창 열기
    입력값 : 없음
    리턴값 : 없음
  *******************************************************************************/
  setMonthPicker3() {
    const modalRef = this.modalService.open(ModalMonthComponent, options);

    modalRef.componentInstance.emitData.subscribe(($e) => {
      this.form.get('date3').setValue($e);
    });

    modalRef.result.then((result) => {
    }, (reason) => {
    });
  }

  /*******************************************************************************
    설  명 : 다음주소창에서 클릭시 처리
    입력값 : 없음
    리턴값 : 없음
  *******************************************************************************/
  setDaumAddressApi(data) {
    // 여기로 주소값이 반환
    this.form.patchValue({
      zipcode: data.zip,
      address: data.addr,
    });

    $("#addressDetail").focus();
  }

}
